import { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useConceptGroupsForCustomCharacteristics } from '@verantos-dev/data-access-concept-groups';
import { DropdownListItem, IconType } from '@verantos-dev/shared-components';
import { CohortSummaryAttributeResult, DomainType } from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { ConceptGroupStats } from './useCustomConceptSelection';
import { mapDomainToApiDomain } from '../utils/domainMappingUtils';

export interface ComboBoxDropdownListItem extends DropdownListItem {
  key: string;
}

/**
 * Hook for mapping between characteristics data and concept groups for custom characteristics
 */
export const useCharacteristicsConceptGroupsMapping = (
  selectedDomain: DomainType | null,
  cohortId?: string
) => {
  const { cohortId: routeCohortId } = useParams();
  const effectiveCohortId = cohortId || routeCohortId;

  const {
    conceptGroups,
    isLoading: isConceptGroupsLoading,
    fetchConceptGroups,
  } = useConceptGroupsForCustomCharacteristics();

  useEffect(() => {
    if (selectedDomain && effectiveCohortId) {
      fetchConceptGroups({
        cohortProfileId: effectiveCohortId,
        domain: mapDomainToApiDomain(selectedDomain, 'lowercase'),
      });
    }
  }, [selectedDomain, effectiveCohortId, fetchConceptGroups]);

  const conceptGroupsByName = useMemo(() => {
    const map = new Map<string, ConceptGroup>();
    conceptGroups.forEach((cg) => {
      map.set(cg.conceptGroupName, cg);
      map.set(cg.conceptGroupName.toLowerCase().trim(), cg);
    });
    return map;
  }, [conceptGroups]);

  const mapCharacteristicsToConceptGroups = useMemo(
    () =>
      (characteristicsData: CohortSummaryAttributeResult[]): ConceptGroup[] => {
        return characteristicsData.map((item) => {
          const matchedConceptGroup =
            conceptGroupsByName.get(item.attributeName) ||
            conceptGroupsByName.get(item.attributeName.toLowerCase().trim());

          if (matchedConceptGroup) {
            return matchedConceptGroup;
          }

          return {
            conceptGroupId: `temp-${item.attributeName.replace(/\s+/g, '-').toLowerCase()}`,
            conceptGroupName: item.attributeName,
            domains: selectedDomain ? mapDomainToApiDomain(selectedDomain, 'capitalized') : '',
            conceptCount: item.attributeCount,
          };
        });
      },
    [conceptGroupsByName, selectedDomain]
  );

  const mapConceptGroupsToCharacteristics = useMemo(
    () =>
      (
        conceptGroups: ConceptGroup[],
        originalCharacteristicsData: CohortSummaryAttributeResult[]
      ): CohortSummaryAttributeResult[] => {
        return conceptGroups.map((cg) => {
          const originalData = originalCharacteristicsData.find(
            (item) =>
              item.attributeName === cg.conceptGroupName ||
              item.attributeName.toLowerCase().trim() === cg.conceptGroupName.toLowerCase().trim()
          );

          return {
            attributeName: cg.conceptGroupName,
            attributeCount: originalData?.attributeCount || cg.conceptCount || 0,
            attributePercentage: originalData?.attributePercentage || 0,
          };
        });
      },
    []
  );

  const mapStatsToConceptGroups = useMemo(
    () =>
      (stats: ConceptGroupStats[]): ConceptGroup[] => {
        return stats.map((stat) => {
          const matchedConceptGroup =
            conceptGroupsByName.get(stat.name) ||
            conceptGroupsByName.get(stat.name.toLowerCase().trim());

          if (matchedConceptGroup) {
            return matchedConceptGroup;
          }

          return {
            conceptGroupId: stat.id,
            conceptGroupName: stat.name,
            domains: selectedDomain ? mapDomainToApiDomain(selectedDomain) : '',
            conceptCount: stat.count,
          };
        });
      },
    [conceptGroupsByName, selectedDomain]
  );

  const mapConceptGroupsToStats = useMemo(
    () =>
      (conceptGroups: ConceptGroup[], originalStats: ConceptGroupStats[]): ConceptGroupStats[] => {
        return conceptGroups.map((cg, index) => {
          const originalStat = originalStats.find(
            (stat) =>
              stat.name === cg.conceptGroupName ||
              stat.name.toLowerCase().trim() === cg.conceptGroupName.toLowerCase().trim()
          );

          return {
            id: originalStat?.id || cg.conceptGroupId || `mapped-${index}`,
            name: cg.conceptGroupName,
            count: originalStat?.count || cg.conceptCount || 0,
            percentage: originalStat?.percentage || 0,
          };
        });
      },
    []
  );

  const mapConceptGroupsToDropdownItems = useMemo(
    () =>
      (conceptGroups: ConceptGroup[]): ComboBoxDropdownListItem[] => {
        return conceptGroups.map((conceptGroup) => {
          const isVerantosCreated = conceptGroup.owner?.toLowerCase?.() === 'verantos';
          const icon: IconType = isVerantosCreated ? 'verantosLogo' : 'owner';

          return {
            key: conceptGroup.conceptGroupId,
            displayValue:
              conceptGroup?.conceptGroupName.charAt?.(0).toUpperCase?.() +
              conceptGroup?.conceptGroupName.slice?.(1),
            value: conceptGroup.conceptGroupId,
            icon,
            actionButtonIcon: 'fullscreen' as IconType,
          };
        });
      },
    []
  );

  return {
    allConceptGroups: conceptGroups,
    isConceptGroupsLoading,
    mapCharacteristicsToConceptGroups,
    mapConceptGroupsToCharacteristics,
    mapStatsToConceptGroups,
    mapConceptGroupsToStats,
    mapConceptGroupsToDropdownItems,
  };
};
